import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaFile } from 'react-icons/fa';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { Flex, Card, SkeletonText, FormLabel, Text, Button, Grid } from '@tq/ui-components';

import { useFileDownload } from 'hooks';
import { ConsultationResponseDocument } from 'types';
import { useFormatLocaleDate, StandardDateFormats } from 'config';

export type UploadedDocumentCardProps = {
  uploadedDocuments: ConsultationResponseDocument[];
  consultationId: number;
  responseId: number;
};

export const UploadedDocumentCard: React.FC<UploadedDocumentCardProps> = ({
  uploadedDocuments,
  consultationId,
  responseId,
}) => {
  const { t } = useTranslation('case_details_card');
  const { openResponseDocument } = useFileDownload();
  const formatLocaleDate = useFormatLocaleDate();

  if (!uploadedDocuments) {
    return <SkeletonText />;
  }

  return (
    <Grid gridGap={{ base: 5, lg: 4 }}>
      {uploadedDocuments.map((document) => (
        <Card
          key={document.id}
          px={{ base: '1.25rem', lg: '1.5rem' }}
          py={{ base: '1.25rem', lg: '1.75rem' }}
        >
          <Flex flexWrap={{ base: 'wrap', lg: 'nowrap' }}>
            <Flex alignItems='center'>
              <FormLabel mb='0' mr={{ base: 2, lg: 4 }}>
                {t('document', 'Document')}:
              </FormLabel>
            </Flex>
            <Flex
              mt={{ base: 2, lg: 0 }}
              mb={{ base: 3, lg: 0 }}
              alignItems='center'
              flex={{ base: '1 1 100%', lg: '1 1 50%' }}
              order={{ base: 1, lg: 0 }}
            >
              <FaFile color={COLOURS.blue[400]} />
              <Text ml={3}>{document.name}</Text>
            </Flex>
            <Flex alignItems='center' flex='1 0 30%' order={{ base: 1, lg: 0 }}>
              <FormLabel mb='0' mr={{ base: 2, lg: 4 }}>
                {t('uploaded', 'Uploaded')}:
              </FormLabel>
              {formatLocaleDate(document.dateModified, StandardDateFormats.dateAndTime)}
            </Flex>
            <Flex alignItems='center' justifyContent='flex-end' flex='1 0 20%'>
              <Button
                size='xs'
                variant='secondary'
                onClick={() =>
                  openResponseDocument({
                    consultationId: consultationId,
                    responseId: responseId,
                    documentId: document.id,
                  })
                }
              >
                {t('view', 'View')}
              </Button>
            </Flex>
          </Flex>
        </Card>
      ))}
    </Grid>
  );
};
